import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { X, Maximize2, Plus, EyeOff, Palette, Volume2, VolumeX } from "lucide-react";
import { useAssistantConversation } from "../../../modules/assistant/state/AssistantConversationProvider";
import { useAssistantProfile, useUpdateAssistantProfile } from "../../../modules/assistant/hooks/useAssistantProfile";
import { useTts } from "../../../modules/assistant/hooks/useTts";
import { useAssistantConfig } from "../useAssistantConfig";
import { NamingStep, shouldShowNaming } from "./NamingStep";
import { Customise } from "./Customise";
import { Conversation } from "./Conversation";
import { StatusLine } from "./StatusLine";
import { StartOverButton } from "./StartOverButton";

/** Every icon button in the popover header. Theme-aware on purpose: these
 *  used to inherit their colour and vanished against a light background. */
const HEADER_BUTTON =
  "p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors";

export function MochiPopover({ onClose, onHideMochi }: { onClose: () => void; onHideMochi: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chat = useAssistantConversation();
  const cfg = useAssistantConfig();
  const rawProfile = useAssistantProfile();
  const updateProfile = useUpdateAssistantProfile();
  const voice = useTts();
  const [customiseOpen, setCustomiseOpen] = useState(false);
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  // RULING 1 (carried over from MochiOverlay): the naming gate reads the raw
  // profile query, not the resolved config block, which always fills in a
  // default name/avatar even before the user has ever touched onboarding.
  const showNaming = shouldShowNaming(rawProfile.data);

  // Voice mute: the resolved profile's effective auto-speak decides the state;
  // the toggle persists the per-user preference and silences any in-flight
  // speech. Only shown when the server actually has TTS wired up — a mute
  // control with nothing to mute would be a dead button.
  const muted = !(rawProfile.data?.resolved?.tts_auto_speak ?? false);
  const toggleMute = () => {
    // currently muted → unmute (true); currently speaking → mute (false).
    updateProfile.mutate({ tts_auto_speak: muted });
    if (!muted) voice.stop(); // muting now: cut off current playback immediately
  };

  useEffect(() => {
    // NamingStep autofocuses its own name input on mount; if the dialog
    // shell also grabs focus, React's child-before-parent passive-effect
    // order means NamingStep focuses first and this effect then yanks focus
    // back to the non-interactive container. Only focus the shell when the
    // naming screen isn't showing — NamingStep owns focus for its own screen.
    if (!showNaming) containerRef.current?.focus();
  }, [showNaming]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="w-[380px] max-w-[92vw] h-[520px] max-h-[70vh] rounded-2xl border border-border bg-background shadow-2xl flex flex-col overflow-hidden outline-none"
    >
      {/* Header actions carried NO colour of their own and inherited whatever
          the page happened to be, which rendered them white-on-white in light
          mode — invisible, while the one button that did set a colour
          (StartOverButton) stayed visible. Icons state their own ink. */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
        <div className="min-w-0">
          <span id={titleId} className="text-sm font-semibold text-foreground">
            {cfg.assistant_name || t("mochi.title")}
          </span>
          <StatusLine />
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            title={t("mochi.customise")}
            aria-label={t("mochi.customise")}
            aria-pressed={customiseOpen}
            onClick={() => setCustomiseOpen((v) => !v)}
            className={HEADER_BUTTON}
          >
            <Palette className="size-4" />
          </button>
          <StartOverButton />
          <button type="button" title={t("mochi.newChat")} aria-label={t("mochi.newChat")} onClick={() => chat.newChat()} className={HEADER_BUTTON}><Plus className="size-4" /></button>
          {voice.ttsEnabled && (
            <button
              type="button"
              title={muted ? t("mochi.unmute", { name: cfg.assistant_name }) : t("mochi.mute", { name: cfg.assistant_name })}
              aria-label={muted ? t("mochi.unmute", { name: cfg.assistant_name }) : t("mochi.mute", { name: cfg.assistant_name })}
              aria-pressed={muted}
              onClick={toggleMute}
              className={HEADER_BUTTON}
            >
              {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
            </button>
          )}
          <button type="button" title={t("mochi.expand", { name: cfg.assistant_name })} aria-label={t("mochi.expand", { name: cfg.assistant_name })} onClick={() => { navigate(`/c/${chat.sessionId}`); onClose(); }} className={HEADER_BUTTON}><Maximize2 className="size-4" /></button>
          <button type="button" title={t("mochi.hide")} aria-label={t("mochi.hide")} onClick={onHideMochi} className={HEADER_BUTTON}><EyeOff className="size-4" /></button>
          <button type="button" title={t("mochi.close")} aria-label={t("mochi.close")} onClick={onClose} className={HEADER_BUTTON}><X className="size-4" /></button>
        </div>
      </div>

      {showNaming ? (
        <NamingStep onDone={() => rawProfile.refetch()} />
      ) : (
        <div className="flex flex-1 min-h-0 flex-col">
          {customiseOpen && <Customise onDone={() => setCustomiseOpen(false)} />}
          <Conversation />
        </div>
      )}
    </div>
  );
}
